import { AnchorIds, TourIds, TourSignal, TourStep, StoryBibleTourVariations } from "../TourTypes";
import { ArrowPosition } from "../StickyNote";
import {
  NewProjectFlowStoryBibleWelcomeContent,
  StoryBibleTourIntroContent,
  StoryBibleWelcomeContent,
  StoryBibleSeedInputContent,
  StoryBibleFinishTourContent,
  StoryBibleSynopsisEditContent,
  StoryBibleRewriteTipContent,
  StoryBibleCastGeneratedContent,
  StoryBibleOutlineGeneratedContent,
  StoryBibleReviewScenesContent,
  StoryBibleAddCharacterContent,
} from "../TourContent";
import InlineSVG from "react-inlinesvg";

const getFirstStepsByVariation = (variation: StoryBibleTourVariations): TourStep[] => {
  switch (variation) {
    case StoryBibleTourVariations.FROM_HELP_MENU:
      // Will render first sticky if on homepage, otherwise the second sticky
      return [
        {
          id: "0",
          stickies: [
            {
              content: (
                <p>
                  👋 Welcome to the Story Bible tour! <strong>Choose a project</strong> to get started.
                </p>
              ),
              offsetX: -100,
              offsetY: -25,
              anchorId: AnchorIds.LOGO,
              rotation: -5,
              arrowPosition: ArrowPosition.Bottom,
            },
            {
              content: (
                <p>
                  👋 Scroll down to <strong>Story Bible</strong> to get started.
                </p>
              ),
              offsetX: 0,
              offsetY: 100,
              anchorId: AnchorIds.EDITOR,
              rotation: 3,
              arrowPosition: ArrowPosition.Bottom,
            },
          ],
          listeningFor: [TourSignal.STORY_BIBLE_SCROLLED_INTO_VIEW],
        },
        {
          id: "0.1",
          stickies: [
            {
              content: <StoryBibleTourIntroContent />,
              offsetX: 250,
              offsetY: 0,
              anchorId: AnchorIds.STORY_BIBLE_SEED,
              rotation: 2,
            },
          ],
          listeningFor: [TourSignal.STICKY_NEXT],
        },
      ];

    case StoryBibleTourVariations.NEW_PROJECT_FLOW:
      return [
        {
          id: "1",
          stickies: [
            {
              content: <NewProjectFlowStoryBibleWelcomeContent />,
              offsetX: 400,
              offsetY: 0,
              anchorId: AnchorIds.STORY_BIBLE_SEED,
              rotation: 2,
              arrowPosition: ArrowPosition.TopLeft,
            },
          ],
          listeningFor: [TourSignal.STICKY_NEXT],
        },
      ];

    case StoryBibleTourVariations.NEW_PROJECT:
    default:
      // If onboarding, start with the welcome content
      return [
        {
          id: "1",
          stickies: [
            {
              content: <StoryBibleWelcomeContent />,
              offsetX: 250,
              offsetY: 0,
              anchorId: AnchorIds.STORY_BIBLE_SEED,
              rotation: 2,
            },
          ],
          listeningFor: [TourSignal.STICKY_NEXT],
        },
      ];
  }
};

const getStoryBibleTour = (variation: StoryBibleTourVariations) => {
  const firstSteps = getFirstStepsByVariation(variation);

  return {
    id: TourIds.STORY_BIBLE,
    variation,
    steps: [
      ...firstSteps,
      {
        id: "1.1",
        stickies: [
          {
            content: <StoryBibleSeedInputContent />,
            offsetX: 250,
            offsetY: 200,
            anchorId: AnchorIds.STORY_BIBLE_SEED,
            rotation: 2,
            arrowPosition: ArrowPosition.Top,
          },
        ],
        listeningFor: [TourSignal.STORY_BIBLE_SEED_INPUT, TourSignal.STICKY_NEXT],
      },
      {
        id: "2",
        stickies: [
          {
            content: (
              <p>
                Now add your <strong>genres</strong> for this project to set the right tone and style.
              </p>
            ),
            offsetX: 250,
            offsetY: 200,
            anchorId: AnchorIds.STORY_BIBLE_GENRE,
            rotation: -2,
            arrowPosition: ArrowPosition.Top,
          },
        ],
        listeningFor: [TourSignal.STORY_BIBLE_GENRE_INPUT],
      },
      {
        id: "3",
        stickies: [
          {
            content: (
              <p>
                Let's pick a <strong>Featured Style </strong> for now, you can always adjust it later.
              </p>
            ),
            offsetX: 0,
            offsetY: 150,
            anchorId: AnchorIds.STORY_BIBLE_STYLE,
            rotation: 3,
            arrowPosition: ArrowPosition.Top,
          },
        ],
        listeningFor: [TourSignal.STORY_BIBLE_STYLE_CLICKED],
      },
      // {
      //   id: "3",
      //   stickies: [
      //     {
      //       content: <StoryBibleStyleTipContent />,
      //       offsetX: -250,
      //       offsetY: 30,
      //       anchorId: AnchorIds.STORY_BIBLE_STYLE_MENU,
      //       rotation: 3,
      //       arrowPosition: ArrowPosition.TopRight,
      //     },
      //   ],
      //   listeningFor: [TourSignal.STICKY_NEXT],
      // },
      {
        id: "4",
        stickies: [
          {
            content: (
              <p>
                Great choice! Now, we can draft a Synopsis. Click the <strong>Generate</strong> button above.
              </p>
            ),
            offsetX: -100,
            offsetY: 45,
            anchorId: AnchorIds.STORY_BIBLE_SYNOPSIS_GENERATE_BUTTON,
            rotation: 3,
            arrowPosition: ArrowPosition.Top,
          },
        ],
        listeningFor: [TourSignal.STORY_BIBLE_SYNOPSIS_GENERATE_CLICKED],
      },
      {
        id: "4.1",
        stickies: [
          {
            content: null,
            offsetX: 0,
            offsetY: 0,
          },
        ],
        listeningFor: [TourSignal.STORY_BIBLE_SYNOPSIS_GENERATE_COMPLETED],
        listeningForFailure: [TourSignal.STORY_BIBLE_SYNOPSIS_GENERATE_FAILED],
      },
      {
        id: "5",
        stickies: [
          {
            content: <StoryBibleSynopsisEditContent />,
            offsetX: 0,
            offsetY: 20,
            anchorId: AnchorIds.STORY_BIBLE_SYNOPSIS_HEADER,
            rotation: 2,
          },
        ],
        listeningFor: [TourSignal.STICKY_NEXT],
      },
      {
        id: "5.1.1",
        stickies: [
          {
            content: <StoryBibleRewriteTipContent />,
            offsetX: 0,
            offsetY: -10,
            arrowPosition: ArrowPosition.Top,
            anchorId: AnchorIds.STORY_BIBLE_SYNOPSIS_END,
            rotation: 2,
          },
        ],
        listeningFor: [TourSignal.STICKY_NEXT],
      },
      {
        id: "6",
        stickies: [
          {
            content: (
              <p>
                Now that you have a synopsis, Sudowrite can generate a <strong>cast of characters</strong>!
              </p>
            ),
            offsetX: 0,
            offsetY: 75,
            anchorId: AnchorIds.STORY_BIBLE_GENERATE_CAST_BUTTON,
            rotation: -2,
            arrowPosition: ArrowPosition.Top,
          },
          {
            content: <StoryBibleAddCharacterContent />,
            offsetX: 0,
            offsetY: 45,
            anchorId: AnchorIds.STORY_BIBLE_ADD_CHARACTER_BUTTON,
            renderIfAnchorMissing: AnchorIds.STORY_BIBLE_GENERATE_CAST_BUTTON,
            rotation: -2,
            arrowPosition: ArrowPosition.Top,
          },
        ],
        listeningFor: [TourSignal.STORY_BIBLE_CAST_GENERATE_CLICKED],
      },
      {
        id: "6.1",
        stickies: [
          {
            content: null,
            offsetX: 0,
            offsetY: 0,
          },
        ],
        listeningFor: [TourSignal.STORY_BIBLE_CAST_GENERATE_COMPLETED],
        listeningForFailure: [TourSignal.STORY_BIBLE_CAST_GENERATE_FAILED],
      },
      {
        id: "6.2",
        stickies: [
          {
            content: <StoryBibleCastGeneratedContent />,
            offsetX: 250,
            offsetY: 20,
            anchorId: AnchorIds.STORY_BIBLE_CHARACTERS,
            rotation: 2,
          },
        ],
        listeningFor: [TourSignal.STICKY_NEXT],
      },
      {
        id: "7",
        stickies: [
          {
            content: (
              <p>
                Before you generate an Outline, click{" "}
                <InlineSVG
                  src="/assets/chevron-down.svg"
                  className="inline-block align-middle"
                />{" "}
                to choose a story structure to guide your plot.
              </p>
            ),
            offsetX: 0,
            offsetY: 45,
            anchorId: AnchorIds.STORY_BIBLE_GENERATE_OUTLINE_BUTTON,
            rotation: 3,
            arrowPosition: ArrowPosition.Top,
          },
        ],
        listeningFor: [TourSignal.STORY_BIBLE_OUTLINE_TYPE_SELECTED],
      },
      {
        id: "8",
        stickies: [
          {
            content: <p>Great choice! Now, click this and Sudowrite will create your outline.</p>,
            offsetX: -50,
            offsetY: 45,
            anchorId: AnchorIds.STORY_BIBLE_GENERATE_OUTLINE_BUTTON,
            rotation: -2,
            arrowPosition: ArrowPosition.Top,
          },
        ],
        listeningFor: [TourSignal.STORY_BIBLE_OUTLINE_GENERATE_CLICKED],
      },
      {
        id: "8.1",
        stickies: [
          {
            content: null,
            offsetX: 0,
            offsetY: 0,
          },
        ],
        listeningFor: [TourSignal.STORY_BIBLE_OUTLINE_GENERATED],
        listeningForFailure: [TourSignal.STORY_BIBLE_OUTLINE_GENERATE_FAILED],
      },
      {
        id: "9",
        stickies: [
          {
            content: <StoryBibleOutlineGeneratedContent />,
            offsetX: 250,
            offsetY: -100,
            anchorId: AnchorIds.STORY_BIBLE_OUTLINE_HEADER,
            rotation: 2,
          },
        ],
        listeningFor: [TourSignal.STICKY_NEXT],
      },
      {
        id: "10",
        stickies: [
          {
            content: <p>🎉 Your story bible is complete! Click here to start working on your first chapter.</p>,
            offsetX: -40,
            offsetY: 35,
            anchorId: AnchorIds.STORY_BIBLE_CREATE_DOCUMENT,
            rotation: -3,
            arrowPosition: ArrowPosition.Top,
          },
        ],
        listeningFor: [TourSignal.STORY_BIBLE_CREATE_DOCUMENT_CLICKED],
      },
      {
        id: "11",
        stickies: [
          {
            content: (
              <p>
                Sudowrite can help you write a first draft. Click{" "}
                <InlineSVG
                  src="/assets/story-bible/generator.svg"
                  className="inline-block align-middle [&_path]:fill-[var(--primary-cta)]"
                />{" "}
                to get started.
              </p>
            ),
            offsetX: -260,
            offsetY: 0,
            anchorId: AnchorIds.STORY_BIBLE_DRAFT_ICON,
            rotation: 2,
            arrowPosition: ArrowPosition.TopRight,
          },
        ],
        listeningFor: [TourSignal.STORY_BIBLE_DRAFT_OPENED],
      },
      {
        id: "12",
        stickies: [
          {
            content: (
              <p>
                Let's <strong>generate the scenes</strong> for this chapter based on your outline.
              </p>
            ),
            offsetX: 0,
            offsetY: 55,
            anchorId: AnchorIds.DRAFT_SCENES_GENERATE,
            rotation: -2,
            arrowPosition: ArrowPosition.Top,
          },
        ],
        listeningFor: [TourSignal.DRAFT_SCENES_GENERATE_COMPLETED],
      },
      {
        id: "12.1",
        stickies: [
          {
            content: <StoryBibleReviewScenesContent />,
            offsetX: -250,
            offsetY: 50,
            anchorId: AnchorIds.STORY_BIBLE_DRAFT_ICON,
            rotation: -2,
          },
        ],
        listeningFor: [TourSignal.STICKY_NEXT],
      },
      {
        id: "13",
        stickies: [
          {
            content: (
              <p>
                Hit <strong>Plan Draft</strong>. We'll check the scenes, just to make sure everything looks good.
              </p>
            ),
            offsetX: -60,
            offsetY: 50,
            anchorId: AnchorIds.DRAFT_PROSE_GENERATE,
            rotation: 3,
            arrowPosition: ArrowPosition.Top,
          },
        ],
        listeningFor: [TourSignal.DRAFT_PROSE_GENERATE_CLICKED],
      },
      {
        id: "13.5",
        stickies: [
          {
            content: (
              <p>
                Sweet! Hit <strong>Generate Draft</strong> to start your story!
              </p>
            ),
            offsetX: -60,
            offsetY: 50,
            anchorId: AnchorIds.DRAFT_PROSE_GENERATE_GO,
            rotation: 3,
            arrowPosition: ArrowPosition.Top,
          },
        ],
        listeningFor: [TourSignal.DRAFT_PROSE_GENERATE_GO_COMPLETED, TourSignal.DRAFT_PROSE_GENERATE_GO_PAUSED],
      },
      {
        id: "14",
        stickies: [
          {
            content: <p>🎉 Amazing! Now you've got a rough draft!</p>,
            offsetX: 0,
            offsetY: -20,
            anchorId: AnchorIds.DRAFT_GENERATE_PANEL,
            rotation: -5,
          },
          {
            content: <StoryBibleFinishTourContent />,
            offsetX: 50,
            offsetY: 100,
            anchorId: AnchorIds.DRAFT_GENERATE_PANEL,
            rotation: -5,
          },
        ],
        listeningFor: [TourSignal.STICKY_NEXT],
      },
    ],
  };
};

export { getStoryBibleTour };
